import type { FailedRefundTransaction } from '~/api/graphql/generated/types'
import i18n from '~/locales/i18n'

import { TransactionFailureReasonId } from '../../../add-payment-dialog-v2/utils/paymentErrorMapping'

/**
 * Error categories for refund transactions
 * Can evolve independently from payment error categories
 */
export enum RefundErrorCategory {
  CONNECTIVITY = 'connectivity',
  ISSUER_DECLINE = 'issuer_decline',
  USER_CANCEL = 'user_cancel',
  CONNECTIVITY_DOWN = 'connectivity_down',
  UNKNOWN = 'unknown',
}

/**
 * Maps transaction failure reason ID to refund error category
 * Uses the same failure reason IDs as payments but can have different category mappings in the future
 */
const FAILURE_REASON_TO_REFUND_CATEGORY_MAP: Record<
  TransactionFailureReasonId,
  RefundErrorCategory
> = {
  [TransactionFailureReasonId.DEVICE_OFFLINE]: RefundErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonId.DEVICE_NOT_FOUND]:
    RefundErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonId.DEVICE_TIMEOUT]: RefundErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonId.DEVICE_BUSY]: RefundErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonId.DEVICE_BATTERY_LOW]:
    RefundErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonId.CANCELLED_BY_DEVICE]:
    RefundErrorCategory.USER_CANCEL,
  [TransactionFailureReasonId.CANCELLED_BY_API]:
    RefundErrorCategory.USER_CANCEL,
  [TransactionFailureReasonId.GATEWAY_REJECTED]:
    RefundErrorCategory.CONNECTIVITY_DOWN,
  [TransactionFailureReasonId.PROCESSOR_DECLINED]:
    RefundErrorCategory.ISSUER_DECLINE,
  [TransactionFailureReasonId.SETTLEMENT_DECLINED]:
    RefundErrorCategory.ISSUER_DECLINE,
  [TransactionFailureReasonId.UNKNOWN]: RefundErrorCategory.UNKNOWN,
}

const getRefundFailureReasonCategory = (
  failureReasonId: number,
): RefundErrorCategory =>
  FAILURE_REASON_TO_REFUND_CATEGORY_MAP[
    failureReasonId as TransactionFailureReasonId
  ] ?? RefundErrorCategory.UNKNOWN

/**
 * Gets translated message for refund error category
 * Translations are computed dynamically, so they update when i18n language changes.
 */
const getRefundCategoryTranslationMessage = (
  category: RefundErrorCategory,
  linked?: boolean,
): string => {
  switch (category) {
    case RefundErrorCategory.ISSUER_DECLINE:
      return linked
        ? i18n.t(
            'Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_ISSUER_DECLINE_LINKED',
          )
        : i18n.t('Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_ISSUER_DECLINE')
    case RefundErrorCategory.CONNECTIVITY:
      return i18n.t('Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_CONNECTIVITY')
    case RefundErrorCategory.UNKNOWN:
      return i18n.t('Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_UNKNOWN')
    case RefundErrorCategory.USER_CANCEL:
      return i18n.t('Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_USER_CANCEL')
    case RefundErrorCategory.CONNECTIVITY_DOWN:
      return i18n.t(
        'Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_CONNECTIVITY_DOWN',
      )
    default:
      return i18n.t('Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_UNKNOWN')
  }
}

const getRefundTransactionFailureTranslationMessage = (
  failureReasonId: number,
  linked?: boolean,
): string => {
  const category = getRefundFailureReasonCategory(failureReasonId)

  try {
    return getRefundCategoryTranslationMessage(category, linked)
  } catch {
    // Fallback to unknown message if translation fails
    return getRefundCategoryTranslationMessage(RefundErrorCategory.UNKNOWN)
  }
}

/**
 * Extracts failure reason ID from FailedRefundTransaction
 * ConstantEntity.id is a GraphQL ID type (string), but contains numeric values
 */
export const extractRefundFailureReasonId = (
  transaction: FailedRefundTransaction,
): number | null => {
  const failureReason = transaction.error?.failureReason

  if (!failureReason) {
    return null
  }
  const { id } = failureReason

  if (typeof id === 'string') {
    const parsedId = parseInt(id, 10)
    return Number.isNaN(parsedId) ? null : parsedId
  }
  if (typeof id === 'number') {
    return id
  }

  return null
}

/**
 * Maps a FailedRefundTransaction to a user-friendly error message
 */
export const getRefundTransactionFailureMessage = (
  transaction: FailedRefundTransaction,
  linked?: boolean,
): string => {
  const failureReasonId = extractRefundFailureReasonId(transaction)

  if (failureReasonId === null) {
    // Fallback to generic message if we can't determine failure reason
    return i18n.t('Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_UNKNOWN')
  }

  return getRefundTransactionFailureTranslationMessage(failureReasonId, linked)
}

/**
 * Maps refund initiation failure to user-friendly error message
 */
export const getRefundInitiationFailureMessage = (): string =>
  i18n.t('Dialogs:REFUND_DIALOG_V2.ERRORS.REFUND_INITIATION_FAILURE')
